import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable } from 'rxjs';

import {
  BaseAuthenticatedService,
  httpQueryParams,
  PaginatedResponse,
} from './base-authenticated.service';
import { PersonalEventInterface } from '@app/interface/personal-event';

/**
 * Accesso alle attività personali dell'utente (model PersonalEvent).
 *
 * Ricalca FiscalEventApi: stesso contratto REST organization-scoped
 * (`api/{organization_id}/personal-event/`) e stessa logica di cancellazione
 * logica via PATCH su `is_deleted`, invece della DELETE.
 */
@Injectable({
  providedIn: 'root',
})
export class PersonalEventApi extends BaseAuthenticatedService {
  constructor(
    public override auth: AngularFireAuth,
    public override httpClient: HttpClient,
  ) {
    super(auth, httpClient);
  }

  /**
   * Una sola pagina di risultati. Chi carica un intervallo di date deve
   * passare `page_size` (il backend accetta al massimo 100) oppure filtrare
   * l'intervallo abbastanza da starci dentro.
   */
  public getEventList<T = PersonalEventInterface>(
    organization_id: string | number | null,
    params: httpQueryParams = {},
  ): Observable<PaginatedResponse<T>> {
    if (!organization_id) {
      throw new Error('Organization ID is required');
    }
    const path = `api/${organization_id}/personal-event/`;
    return this.getList<T>(path, params);
  }

  public createPersonalEvent(
    organization_id: number | null,
    personalEventData: Partial<PersonalEventInterface>,
  ): Observable<PersonalEventInterface> {
    const path = `api/${organization_id}/personal-event/`;
    return this.post<PersonalEventInterface>(path, {}, personalEventData);
  }

  public updatePersonalEventDetails(
    organization_id: number | null,
    id: number,
    data: Partial<PersonalEventInterface>,
  ): Observable<PersonalEventInterface> {
    const path = `api/${organization_id}/personal-event/${id}/`;
    return this.partialUpdate<PersonalEventInterface>(path, {}, data);
  }

  /** Spunta o despunta un'attività senza toccare gli altri campi. */
  public setPersonalEventDone(
    organization_id: number | null,
    id: number,
    is_done: boolean,
  ): Observable<PersonalEventInterface> {
    return this.updatePersonalEventDetails(organization_id, id, { is_done });
  }

  /** Cancellazione logica: la riga resta a DB ma sparisce dalla bacheca. */
  public softDeletePersonalEvent(
    organization_id: number | null,
    id: number,
  ): Observable<PersonalEventInterface> {
    return this.updatePersonalEventDetails(organization_id, id, {
      is_deleted: true,
    });
  }
}
